/**
 * Decides whether a room change should clear the map-editor state, and which room the scope becomes.
 *
 * Kept as a pure module of its own rather than folded into `helpers.ts`, which reaches for a bare
 * `@interfaces` alias that the Jest resolver does not map — importing it drags that in and makes the
 * rule untestable without touching the build config.
 *
 * The rule is easy to get wrong in two opposite directions, and neither failure shows up in the
 * effect that calls it. `currentRoom` is derived: `useParticipantsState` blanks it on any pass where
 * the participant's own roomId is empty (see the reload-rebind note in the provider), so reading that
 * null as a departure throws away a live geometry report and leaves the editor blind until Unreal
 * happens to send another. Reading the *first* room as a change is pointless — there is nothing to
 * clear yet — and would discard a report that arrived before the room resolved.
 *
 * @param previousRoomId - Room the map-editor state currently describes; null before the first one.
 * @param roomId - Room now current, or null while the derived value is blank.
 * @returns `scope` to remember, and `shouldClear` — true only for a move between two real rooms.
 */
export const resolveMapEditorRoomScope = (
  previousRoomId: string | null,
  roomId: string | null,
): { scope: string | null; shouldClear: boolean } => {
  if (!roomId || roomId === previousRoomId) {
    return { scope: previousRoomId, shouldClear: false };
  }

  return { scope: roomId, shouldClear: previousRoomId !== null };
};
